import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import toast from 'react-hot-toast';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { adminAPI } from '../services/api';
import { Gift, Crown, Layers, AlertTriangle, Plus, Edit2, Trash } from 'lucide-react';

const Container = styled.div`
  max-width: 1100px;
  margin: 0 auto;
  padding: ${props => props.theme.spacing.lg};
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.lg};
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.sm};
`;

const Title = styled.h1`
  font-size: 2rem;
  font-weight: 700;
  color: ${props => props.theme.colors.text};
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
`;

const Subtitle = styled.p`
  color: ${props => props.theme.colors.textSecondary};
  max-width: 720px;
  line-height: 1.5;
`;

const Grid = styled.div`
  display: grid;
  gap: ${props => props.theme.spacing.lg};
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
`;

const Card = styled.div`
  background: ${props => props.theme.colors.surface};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.xl};
  padding: ${props => props.theme.spacing.lg};
  box-shadow: ${props => props.theme.shadows.sm};
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.md};
  position: relative;
`;

const FeaturedBadge = styled.span`
  position: absolute;
  top: ${props => props.theme.spacing.md};
  right: ${props => props.theme.spacing.md};
  background: ${props => props.theme.colors.primary};
  color: #fff;
  font-size: 0.75rem;
  padding: 4px 8px;
  border-radius: ${props => props.theme.borderRadius.sm};
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const PlanName = styled.h2`
  font-size: 1.5rem;
  font-weight: 600;
  color: ${props => props.theme.colors.text};
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
`;

const Price = styled.div`
  font-size: 2rem;
  font-weight: 700;
  color: ${props => props.theme.colors.primary};
`;

const Description = styled.p`
  color: ${props => props.theme.colors.textSecondary};
  line-height: 1.5;
`;

const LimitsList = styled.ul`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.xs};
  margin: 0;
  padding-left: ${props => props.theme.spacing.lg};
  color: ${props => props.theme.colors.textSecondary};
  font-size: 0.95rem;
`;

const EmptyState = styled.div`
  padding: ${props => props.theme.spacing.xxl};
  text-align: center;
  color: ${props => props.theme.colors.textSecondary};
  background: ${props => props.theme.colors.background};
  border-radius: ${props => props.theme.borderRadius.md};
  border: 1px dashed ${props => props.theme.colors.border};
`;

const InfoBox = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${props => props.theme.spacing.sm};
  background: ${props => props.theme.colors.background};
  border: 1px dashed ${props => props.theme.colors.borderMuted};
  border-radius: ${props => props.theme.borderRadius.md};
  padding: ${props => props.theme.spacing.md};
  color: ${props => props.theme.colors.textSecondary};
  font-size: 0.9rem;
`;

const ActionsBar = styled.div`
  display: flex;
  justify-content: flex-end;
`;

const PrimaryButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: ${props => props.theme.colors.primary};
  border: none;
  color: white;
  padding: 10px 16px;
  border-radius: ${props => props.theme.borderRadius.md};
  font-weight: 600;
  cursor: pointer;
  transition: transform 0.1s ease, box-shadow 0.1s ease;

  &:hover {
    transform: translateY(-1px);
    box-shadow: ${props => props.theme.shadows.md};
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

const CardActions = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing.sm};
`;

const GhostButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: transparent;
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.sm};
  padding: 8px 12px;
  color: ${props => props.theme.colors.textSecondary};
  font-size: 0.9rem;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease, border 0.15s ease;

  &:hover {
    background: ${props => props.theme.colors.surfaceMuted};
    color: ${props => props.theme.colors.text};
  }

  &.danger {
    border-color: ${props => props.theme.colors.error};
    color: ${props => props.theme.colors.error};

    &:hover {
      background: rgba(239, 68, 68, 0.1);
    }
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.35);
  display: flex;
  justify-content: center;
  align-items: center;
  padding: ${props => props.theme.spacing.lg};
  z-index: 1000;
`;

const ModalContent = styled.div`
  width: min(720px, 100%);
  background: ${props => props.theme.colors.surface};
  border-radius: ${props => props.theme.borderRadius.xl};
  box-shadow: ${props => props.theme.shadows.lg};
  padding: ${props => props.theme.spacing.xl};
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.lg};
`;

const ModalTitle = styled.h3`
  margin: 0;
  font-size: 1.5rem;
  font-weight: 600;
  color: ${props => props.theme.colors.text};
`;

const FormGrid = styled.div`
  display: grid;
  gap: ${props => props.theme.spacing.md};
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
`;

const Field = styled.label`
  display: flex;
  flex-direction: column;
  gap: 6px;
  font-size: 0.9rem;
  color: ${props => props.theme.colors.textSecondary};
`;

const Input = styled.input`
  padding: 10px 12px;
  border-radius: ${props => props.theme.borderRadius.md};
  border: 1px solid ${props => props.theme.colors.border};
  background: ${props => props.theme.colors.surfaceMuted};
  color: ${props => props.theme.colors.text};
  font-size: 0.95rem;

  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
    box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.15);
  }
`;

const TextArea = styled.textarea`
  padding: 10px 12px;
  border-radius: ${props => props.theme.borderRadius.md};
  border: 1px solid ${props => props.theme.colors.border};
  background: ${props => props.theme.colors.surfaceMuted};
  color: ${props => props.theme.colors.text};
  font-size: 0.95rem;
  min-height: 120px;
  resize: vertical;

  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
    box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.15);
  }
`;

const CheckboxRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 0;
  color: ${props => props.theme.colors.textSecondary};
  font-size: 0.95rem;
`;

const ModalActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: ${props => props.theme.spacing.sm};
`;

const SecondaryButton = styled.button`
  padding: 10px 16px;
  border-radius: ${props => props.theme.borderRadius.md};
  border: 1px solid ${props => props.theme.colors.border};
  background: ${props => props.theme.colors.surfaceMuted};
  color: ${props => props.theme.colors.text};
  cursor: pointer;

  &:hover {
    background: ${props => props.theme.colors.surface};
  }
`;

function BillingPlans() {
  const queryClient = useQueryClient();
  const [modalState, setModalState] = useState({ open: false, mode: 'create', plan: null });
  const [formState, setFormState] = useState({
    plan_key: '',
    name: '',
    description: '',
    price: '',
    currency: 'RUB',
    duration_days: 30,
    limits: '{}',
    is_active: true,
    is_featured: false,
  });

  const { data, isLoading, error } = useQuery(
    'admin.billing.plans',
    () => adminAPI.getBillingPlans().then(res => res.data),
    {
      refetchInterval: 60000,
    }
  );

  const createPlanMutation = useMutation(
    payload => adminAPI.createBillingPlan(payload).then(res => res.data),
    {
      onSuccess: () => {
        toast.success('План успешно создан');
        queryClient.invalidateQueries('admin.billing.plans');
        closeModal();
      },
      onError: err => {
        toast.error(err.response?.data?.detail || 'Не удалось создать план');
      },
    }
  );

  const updatePlanMutation = useMutation(
    ({ id, payload }) => adminAPI.updateBillingPlan(id, payload).then(res => res.data),
    {
      onSuccess: () => {
        toast.success('План обновлён');
        queryClient.invalidateQueries('admin.billing.plans');
        closeModal();
      },
      onError: err => {
        toast.error(err.response?.data?.detail || 'Не удалось обновить план');
      },
    }
  );

  const deletePlanMutation = useMutation(
    planId => adminAPI.deleteBillingPlan(planId).then(res => res.data),
    {
      onSuccess: () => {
        toast.success('План удалён');
        queryClient.invalidateQueries('admin.billing.plans');
      },
      onError: err => {
        toast.error(err.response?.data?.detail || 'Не удалось удалить план');
      },
    }
  );

  const iconForPlan = planId => {
    if (planId?.includes('premium')) return <Crown size={24} />;
    if (planId?.includes('free')) return <Gift size={24} />;
    return <Layers size={24} />;
  };

  const openCreateModal = () => {
    setFormState({
      plan_key: '',
      name: '',
      description: '',
      price: '',
      currency: 'RUB',
      duration_days: 30,
      limits: '{\n  "requests_per_day": 100\n}',
      is_active: true,
      is_featured: false,
    });
    setModalState({ open: true, mode: 'create', plan: null });
  };

  const openEditModal = plan => {
    setFormState({
      plan_key: plan.plan_key,
      name: plan.name || '',
      description: plan.description || '',
      price: plan.price ?? '',
      currency: plan.currency || 'RUB',
      duration_days: plan.duration_days ?? 30,
      limits: JSON.stringify(plan.limits || {}, null, 2),
      is_active: Boolean(plan.is_active),
      is_featured: Boolean(plan.is_featured),
    });
    setModalState({ open: true, mode: 'edit', plan });
  };

  const closeModal = () => {
    setModalState({ open: false, mode: 'create', plan: null });
  };

  const handleFormChange = evt => {
    const { name, value, type, checked } = evt.target;
    setFormState(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSubmit = evt => {
    evt.preventDefault();
    let limits = {};
    if (formState.limits?.trim()) {
      try {
        limits = JSON.parse(formState.limits);
      } catch (err) {
        toast.error('Некорректный JSON в поле "Лимиты"');
        return;
      }
    }

    const payload = {
      plan_key: formState.plan_key.trim(),
      name: formState.name.trim(),
      description: formState.description?.trim() || null,
      price: formState.price === '' ? 0 : Number(formState.price),
      currency: formState.currency || 'RUB',
      duration_days: Number(formState.duration_days) || 30,
      limits,
      is_active: formState.is_active,
      is_featured: formState.is_featured,
    };

    if (!payload.plan_key && modalState.mode === 'create') {
      toast.error('Укажите уникальный ключ плана');
      return;
    }
    if (!payload.name) {
      toast.error('Введите название плана');
      return;
    }

    if (modalState.mode === 'create') {
      createPlanMutation.mutate(payload);
    } else if (modalState.plan) {
      const { plan } = modalState;
      const { plan_key, ...rest } = payload; // ключ менять нельзя
      updatePlanMutation.mutate({ id: plan.id, payload: rest });
    }
  };

  const handleDelete = plan => {
    if (window.confirm(`Удалить план "${plan.name}"?`)) {
      deletePlanMutation.mutate(plan.id);
    }
  };

  const isSubmitting =
    createPlanMutation.isLoading || updatePlanMutation.isLoading || deletePlanMutation.isLoading;

  const plans = useMemo(() => data?.plans ?? [], [data]);

  if (isLoading) {
    return <EmptyState>Загрузка тарифов...</EmptyState>;
  }

  if (error) {
    return (
      <EmptyState>
        <AlertTriangle size={18} /> Не удалось загрузить список тарифов
      </EmptyState>
    );
  }

  return (
    <Container>
      <Header>
        <Title>
          <Layers size={32} />
          Тарифные планы
        </Title>
        <Subtitle>
          Управляйте тарифами, доступными в WebApp: создавайте новые планы, обновляйте цены и лимиты,
          скрывайте неактуальные предложения. Все изменения применяются мгновенно.
        </Subtitle>
      </Header>

      <InfoBox>
        <AlertTriangle size={18} />
        <div>
          <strong>Подсказка.</strong> Значение «Лимиты» задаётся в формате JSON. Вы можете добавить
          любые ключи (например, <code>requests_per_day</code>, <code>image_generations</code>),
          далее они попадут в WebApp и бота.
        </div>
      </InfoBox>

      <ActionsBar>
        <PrimaryButton onClick={openCreateModal}>
          <Plus size={18} />
          Новый план
        </PrimaryButton>
      </ActionsBar>

      {plans.length ? (
        <Grid>
          {plans.map(plan => (
            <Card key={plan.id}>
              {plan.is_featured && <FeaturedBadge>Рекомендуем</FeaturedBadge>}
              <PlanName>
                {iconForPlan(plan.plan_key)}
                {plan.name}
              </PlanName>
              <Price>
                {plan.price ? `₽${plan.price}` : 'Бесплатно'}
                <span style={{ fontSize: '1rem', color: '#6b7280', marginLeft: '4px' }}>
                  / {plan.duration_days} дн.
                </span>
              </Price>
              <Description>{plan.description || 'Подписка без описания'}</Description>
              <LimitsList>
                {plan.limits
                  ? Object.entries(plan.limits).map(([key, value]) => {
                      // Форматируем лимиты для красивого отображения
                      let displayText = `${key}: ${value}`;
                      switch (key) {
                        case 'requests_per_day':
                          displayText = `До ${value} запросов в день`;
                          break;
                        case 'requests_per_week':
                          displayText = `До ${value} запросов в неделю`;
                          break;
                        case 'weekly_requests':
                          displayText = `До ${value} запросов в неделю`;
                          break;
                        case 'daily_requests':
                          displayText = `До ${value} запросов в день`;
                          break;
                        case 'image_generations':
                          displayText = `${value} генераций изображений`;
                          break;
                        case 'video_generations':
                          displayText = `${value} видео-генераций`;
                          break;
                        default:
                          displayText = `${key}: ${value}`;
                      }
                      return (
                        <li key={key}>
                          {displayText}
                        </li>
                      );
                    })
                  : <li>Лимиты не заданы</li>}
              </LimitsList>
              <CardActions>
                <GhostButton onClick={() => openEditModal(plan)}>
                  <Edit2 size={16} />
                  Редактировать
                </GhostButton>
                <GhostButton className="danger" onClick={() => handleDelete(plan)}>
                  <Trash size={16} />
                  Удалить
                </GhostButton>
              </CardActions>
            </Card>
          ))}
        </Grid>
      ) : (
        <EmptyState>Тарифы не найдены</EmptyState>
      )}

      {modalState.open && (
        <ModalOverlay>
          <ModalContent as="form" onSubmit={handleSubmit}>
            <ModalTitle>
              {modalState.mode === 'create' ? 'Новый тарифный план' : 'Редактирование плана'}
            </ModalTitle>

            <FormGrid>
              {modalState.mode === 'create' && (
                <Field>
                  Ключ плана (латиница, уникально)
                  <Input
                    name="plan_key"
                    value={formState.plan_key}
                    onChange={handleFormChange}
                    placeholder="Например, starter_monthly"
                    required
                  />
                </Field>
              )}

              <Field>
                Название
                <Input
                  name="name"
                  value={formState.name}
                  onChange={handleFormChange}
                  placeholder="Starter"
                  required
                />
              </Field>

              <Field>
                Цена (₽)
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  name="price"
                  value={formState.price}
                  onChange={handleFormChange}
                  placeholder="499"
                />
              </Field>

              <Field>
                Валюта
                <Input
                  name="currency"
                  value={formState.currency}
                  onChange={handleFormChange}
                  placeholder="RUB"
                />
              </Field>

              <Field>
                Длительность (в днях)
                <Input
                  type="number"
                  min="1"
                  name="duration_days"
                  value={formState.duration_days}
                  onChange={handleFormChange}
                />
              </Field>
            </FormGrid>

            <Field>
              Описание
              <TextArea
                name="description"
                value={formState.description}
                onChange={handleFormChange}
                placeholder="Краткое описание тарифа"
              />
            </Field>

            <Field>
              Лимиты (JSON)
              <TextArea
                name="limits"
                value={formState.limits}
                onChange={handleFormChange}
                spellCheck={false}
              />
            </Field>

            <FormGrid>
              <CheckboxRow>
                <input
                  id="plan-active"
                  type="checkbox"
                  name="is_active"
                  checked={formState.is_active}
                  onChange={handleFormChange}
                />
                <label htmlFor="plan-active">План активен</label>
              </CheckboxRow>

              <CheckboxRow>
                <input
                  id="plan-featured"
                  type="checkbox"
                  name="is_featured"
                  checked={formState.is_featured}
                  onChange={handleFormChange}
                />
                <label htmlFor="plan-featured">Пометить как рекомендуемый</label>
              </CheckboxRow>
            </FormGrid>

            <ModalActions>
              <SecondaryButton type="button" onClick={closeModal} disabled={isSubmitting}>
                Отмена
              </SecondaryButton>
              <PrimaryButton type="submit" disabled={isSubmitting}>
                {modalState.mode === 'create' ? 'Создать план' : 'Сохранить изменения'}
              </PrimaryButton>
            </ModalActions>
          </ModalContent>
        </ModalOverlay>
      )}
    </Container>
  );
}

export default BillingPlans;

